import React, { useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Stack,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    Skeleton,
    Avatar,
    MenuItem,
    Grid,
    IconButton,
    InputAdornment,
    Drawer,
    Divider,
    Tooltip,
    Badge
} from '@mui/material';
import { Search as SearchIcon, Verified, Moped, PersonAdd, CheckCircle, HourglassEmpty, Block, Settings } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAdminAllRiders, useAdminUpdateRider } from 'hooks/useRiders';
import FilterDrawer from 'components/common/FilterDrawer';
import PermissionGuard from 'ui-component/PermissionGuard';
import { FilterList } from '@mui/icons-material';

const statusOptions = ['All', 'verified', 'pending', 'suspended'];

export default function RiderList() {
    const navigate = useNavigate();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [selectedRider, setSelectedRider] = useState(null);

    const [filterOpen, setFilterOpen] = useState(false);
    const [filters, setFilters] = useState({});

    const {
        data: riderRes,
        isLoading,
        refetch
    } = useAdminAllRiders({
        page: page + 1,
        limit: rowsPerPage,
        search: search || undefined,
        status: statusFilter !== 'All' ? statusFilter : undefined,
        ...filters
    });

    const handleFilterApply = (newFilters) => {
        setFilters(newFilters);
        setPage(0);
    };

    const filterConfig = [
        { name: 'vehicle', label: 'Vehicle Type', type: 'text' },
        { name: 'startDate', label: 'Start Date', type: 'date' },
        { name: 'endDate', label: 'End Date', type: 'date' },
        {
            name: 'sortBy',
            label: 'Sort By',
            type: 'select',
            options: [
                { label: 'Newest', value: 'createdAt' },
                { label: 'Name', value: 'firstName' },
                { label: 'Wallet', value: 'walletBalance' }
            ]
        },
        {
            name: 'sortOrder',
            label: 'Sort Order',
            type: 'select',
            options: [
                { label: 'Descending', value: 'desc' },
                { label: 'Ascending', value: 'asc' }
            ]
        }
    ];

    const riders = riderRes?.data || [];
    const totalRiders = riderRes?.total || 0;

    const getStatusStyle = (status) => {
        switch (status?.toLowerCase()) {
            case 'verified':
                return { color: 'success', icon: <Verified sx={{ fontSize: 14 }} /> };
            case 'pending':
                return { color: 'warning', icon: <HourglassEmpty sx={{ fontSize: 14 }} /> };
            case 'suspended':
                return { color: 'error', icon: <Block sx={{ fontSize: 14 }} /> };
            default:
                return { color: 'default', icon: null };
        }
    };

    return (
        <Box p={3} sx={{ background: '#f5f6fa', minHeight: '100vh' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
                <Typography variant="h4" fontWeight={700}>
                    Fleet Management
                </Typography>
                {/* <Button
                    variant="contained"
                    startIcon={<PersonAdd />}
                    onClick={() => navigate('/admin/riders/new')}
                    sx={{ borderRadius: 2, px: 3 }}
                >
                    Onboard New Rider
                </Button> */}
            </Stack>

            {/* 1. Rider Stats Overview */}
            <Grid container spacing={3} mb={4}>
                <RiderStatCard title="Total Fleet" value={totalRiders} color="#1976d2" icon={<Moped color="primary" />} />
                <RiderStatCard
                    title="KYC Pending"
                    value={riders.filter((r) => r.user?.kycStatus === 'pending').length}
                    color="#ed6c02"
                    icon={<HourglassEmpty color="warning" />}
                />
                <RiderStatCard
                    title="Active/Online"
                    value={riders.filter((r) => r.available).length}
                    color="#2e7d32"
                    icon={<CheckCircle color="success" />}
                />
            </Grid>

            <Paper sx={{ width: '100%', borderRadius: 3, p: 2, overflow: 'hidden' }}>
                {/* 🔍 Search & Filters */}
                <Stack direction="row" spacing={2} mb={3}>
                    <TextField
                        placeholder="Search Name, Phone or Email..."
                        size="small"
                        sx={{ width: 350 }}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" fontSize="small" />
                                </InputAdornment>
                            )
                        }}
                    />
                    <TextField
                        select
                        size="small"
                        label="Account Status"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        sx={{ minWidth: 180 }}
                    >
                        {statusOptions.map((opt) => (
                            <MenuItem key={opt} value={opt}>
                                {opt.toUpperCase()}
                            </MenuItem>
                        ))}
                    </TextField>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 2 }}>
                        <Badge badgeContent={Object.keys(filters).length} color="primary">
                            <Button variant="outlined" startIcon={<FilterList />} onClick={() => setFilterOpen(true)}>
                                Filters
                            </Button>
                        </Badge>
                        {Object.keys(filters).length > 0 && (
                            <Button variant="text" onClick={() => setFilters({})} color="error" size="small">
                                Reset
                            </Button>
                        )}
                    </Stack>
                </Stack>

                <TableContainer>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>Rider Info</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Vehicle</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Availability</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Account Status</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>KYC Status</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>
                                    Actions
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading
                                ? [...Array(5)].map((_, i) => (
                                      <TableRow key={i}>
                                          <TableCell colSpan={6}>
                                              <Skeleton height={50} />
                                          </TableCell>
                                      </TableRow>
                                  ))
                                : riders.map((rider) => (
                                      <TableRow key={rider.id} hover>
                                          <TableCell>
                                              <Stack direction="row" spacing={2} alignItems="center">
                                                  <Avatar sx={{ bgcolor: 'primary.light', fontWeight: 700 }}>
                                                      {rider.user?.firstName?.[0]}
                                                  </Avatar>
                                                  <Box>
                                                      <Typography variant="subtitle2" sx={{ textTransform: 'capitalize' }}>
                                                          {rider.user?.firstName} {rider.user?.lastName}
                                                      </Typography>
                                                      <Typography variant="caption" color="text.secondary">
                                                          {rider.phoneNumber}
                                                      </Typography>
                                                  </Box>
                                              </Stack>
                                          </TableCell>

                                          <TableCell>
                                              <Typography variant="body2" sx={{ textTransform: 'capitalize', fontWeight: 500 }}>
                                                  {rider.vehicle || '---'}
                                              </Typography>
                                          </TableCell>

                                          <TableCell>
                                              <Chip
                                                  label={rider.available ? 'ONLINE' : 'OFFLINE'}
                                                  size="small"
                                                  color={rider.available ? 'success' : 'default'}
                                                  variant={rider.available ? 'filled' : 'outlined'}
                                                  sx={{ fontWeight: 700, fontSize: '0.65rem' }}
                                              />
                                          </TableCell>

                                          <TableCell>
                                              <Chip
                                                  label={rider.status}
                                                  size="small"
                                                  icon={getStatusStyle(rider.status).icon}
                                                  color={getStatusStyle(rider.status).color}
                                                  sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.65rem' }}
                                              />
                                          </TableCell>

                                          <TableCell>
                                              <Typography
                                                  variant="caption"
                                                  fontWeight={700}
                                                  color={rider.user?.kycStatus === 'verified' ? 'success.main' : 'warning.main'}
                                              >
                                                  {rider.user?.kycStatus?.toUpperCase()}
                                              </Typography>
                                          </TableCell>

                                          <TableCell align="right">
                                              <PermissionGuard permission="manage_riders" action="view">
                                                  <IconButton color="primary" onClick={() => setSelectedRider(rider)}>
                                                      <Settings fontSize="small" />
                                                  </IconButton>
                                              </PermissionGuard>
                                          </TableCell>
                                      </TableRow>
                                  ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[10, 25]}
                    component="div"
                    count={totalRiders}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            </Paper>

            <RiderControlDrawer open={!!selectedRider} rider={selectedRider} onClose={() => setSelectedRider(null)} onUpdate={refetch} />

            <FilterDrawer
                open={filterOpen}
                onClose={() => setFilterOpen(false)}
                onApply={handleFilterApply}
                initialFilters={filters}
                config={filterConfig}
            />
        </Box>
    );
}

// --- Internal Stat Card ---
function RiderStatCard({ title, value, icon, color }) {
    return (
        <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2.5, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        {title.toUpperCase()}
                    </Typography>
                    <Typography variant="h5" fontWeight={800}>
                        {value}
                    </Typography>
                </Box>
                <Avatar sx={{ bgcolor: `${color}15`, p: 1 }}>{icon}</Avatar>
            </Paper>
        </Grid>
    );
}

// --- Quick Management Drawer ---
function RiderControlDrawer({ open, rider, onClose, onUpdate }) {
    const navigate = useNavigate();
    const { mutate: updateRider, isPending: isUpdating } = useAdminUpdateRider();

    const handleUpdateSuccess = () => {
        onUpdate();
    };

    // Update onSuccess of the mutation in the onClick or configure it in the hook, 
    // but since we don't have onSuccess in the hook here, we will just call onUpdate after a short delay
    // Actually, we can pass onSuccess to mutate:
    const handleToggle = () => {
        updateRider(
            { id: rider.id, payload: { available: !rider.available } },
            { onSuccess: () => onUpdate() }
        );
    };

    if (!rider) return null;
    return (
        <Drawer anchor="right" open={open} onClose={onClose}>
            <Box sx={{ width: 400, p: 3 }}>
                <Typography variant="h6" fontWeight={700}>
                    Rider Control Center
                </Typography>
                <Divider sx={{ my: 2 }} />

                <Stack spacing={3}>
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                        <Avatar sx={{ width: 80, height: 80, mx: 'auto', mb: 2, bgcolor: 'primary.main', fontSize: '2rem' }}>
                            {rider.user?.firstName?.[0]}
                        </Avatar>
                        <Typography variant="h6">
                            {rider.user?.firstName} {rider.user?.lastName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {rider.phoneNumber}
                        </Typography>
                        <Chip
                            label={rider.available ? '🟢 Online' : '🔴 Offline'}
                            color={rider.available ? 'success' : 'default'}
                            size="small"
                            variant={rider.available ? 'filled' : 'outlined'}
                            sx={{ mt: 0.5, fontWeight: 700 }}
                        />
                        <Box mt={1}>
                            <Button
                                variant="outlined"
                                size="small"
                                color={rider.available ? 'error' : 'success'}
                                onClick={handleToggle}
                                disabled={isUpdating}
                            >
                                {isUpdating ? 'Updating...' : `Set ${rider.available ? 'Offline' : 'Online'}`}
                            </Button>
                        </Box>
                    </Box>

                    <Box sx={{ bgcolor: '#f9f9f9', p: 2, borderRadius: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Administrative Actions
                        </Typography>
                        <Stack spacing={1} mt={1}>
                            <Button
                                fullWidth
                                variant="contained"
                                color="success"
                                onClick={() => {
                                    navigate(`/admin/riders/${rider.id}`);
                                    onClose();
                                }}
                            >
                                View
                            </Button>
                        </Stack>
                    </Box>

                    <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                            Vehicle Details
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                            {rider.vehicle || 'Not Specified'}
                        </Typography>
                    </Box>

                    <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                            Current Wallet Balance
                        </Typography>
                        <Typography variant="body2" fontWeight={600} color="primary.main">
                            ₦{rider.walletBalance?.toLocaleString() || '0'}
                        </Typography>
                    </Box>
                </Stack>
            </Box>
        </Drawer>
    );
}
